import { ReactElement, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Link,
  Skeleton,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  CertificateIcon,
  DownloadIcon,
  LeftArrowIcon,
  RefreshIcon,
} from 'src/v3/core/icons'
import { selectUser } from 'src/v3/settings/settings.slice'
import {
  certificatePollRequested,
  selectCertificatePollByCourseID,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { RestartCourseButton } from 'src/v3/classroom-v2/components/restart-course-control'
import { isCertificateResolved } from 'src/v3/classroom-v2/utils/api'
import {
  CERTIFICATE_POLL_MAX_ATTEMPTS,
  isCertificateGenerationSlow,
} from 'src/v3/classroom-v2/utils/certificate-poll'
import {
  CourseV2CAPCEAccreditation,
  CourseV2Certificate,
  CourseV2CertificateRequirement,
  CourseV2Content,
  CourseV2IssuedCertificate,
} from 'src/v3/classroom-v2/types'

// What's still owed, in the learner's words. Same job as survey-step's
// prerequisiteLabel, and phrased the same way — a list of things to go do, not an
// error report.
let requirementLabel = (
  requirement: CourseV2CertificateRequirement,
): string => {
  if (requirement === 'certificateCourseStepsIncomplete') {
    return 'Finish every course step.'
  }
  return requirement === 'certificateQuizNotPassed'
    ? 'Pass the course assessment.'
    : 'Submit the course survey.'
}

let formatCertificateDate = (value: string) =>
  dayjs(value).format('MMMM D, YYYY')

let formatCEHours = (length: number) =>
  Number.isInteger(length) ? length.toFixed(1) : String(length)

// The theme's heading variants carry ABSOLUTE line heights (h1: 112px, h2: 72px,
// h3: 56px) sized for their full display font. Every heading on this surface scales
// the font down, and fontSize alone doesn't touch lineHeight — so a heading that
// wraps gets the original px gap between its lines, which is where the huge gaps in
// a long course name came from. Overriding with a unitless ratio makes the leading
// track whatever size the heading actually renders at.
let HEADING_LINE_HEIGHT = 1.25

// A summary of what's in the PDF, deliberately NOT a rendition of it. This card
// used to open "This certifies that <name>" and read as the certificate — which it
// isn't and can't be: the PDF is the only document that certifies anything, and a
// web page that talks like one invites a screenshot being passed off as evidence.
// So it describes the file the learner is about to download and nothing more.
let CertificateSummary = ({
  content,
  learnerName,
  certificate,
}: {
  content: CourseV2Content
  learnerName: string | null
  certificate: CourseV2IssuedCertificate
}): ReactElement => {
  let theme = useTheme()
  // The completion date, and specifically requirementsCompletionDate rather than
  // issuedTime: what the learner completed is the fact being certified, whereas
  // when a Lambda rendered a PDF is an implementation detail that could differ by
  // minutes or, after a re-render, by days. Falls back through the other two
  // rather than showing nothing, since all three are individually omissible.
  let completedDate =
    certificate.requirementsCompletionDate ??
    certificate.educationalEventDate ??
    certificate.issuedTime
  // Narrowed with a predicate rather than a bare find so courseCode is reachable
  // — same shape course-overview.tsx uses for the same lookup.
  let capce = content.accreditation.find(
    (a): a is CourseV2CAPCEAccreditation => a.kind === 'capce',
  )

  return (
    <Card
      variant="outlined"
      sx={{
        p: 4,
        textAlign: 'center',
        borderTop: `3px solid ${theme.palette.primary.main}`,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: 2, display: 'block' }}
      >
        Ready to download
      </Typography>

      {/* Named as the subject of the certificate, not addressed as if this card
          were conferring it. The name is omitted rather than guessed at when the
          user record hasn't loaded: a blank where a name belongs reads worse than
          not restating who you are, and the PDF carries the authoritative name
          either way. */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {learnerName ? `CE certificate for ${learnerName}` : 'CE certificate'}
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontSize: '1.25rem',
            lineHeight: HEADING_LINE_HEIGHT,
            fontWeight: 'fontWeightBold',
            // Roomier than the 0.5 this had while the heading's leading was still
            // the theme's 56px — that gap was doing the visual spacing by
            // accident. Now that the line height is tight, the space between the
            // label and the course name has to be actual margin.
            mt: 4,
          }}
        >
          {content.name}
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-around',
          gap: 2,
          textAlign: 'left',
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block' }}
          >
            CE HOURS
          </Typography>
          <Typography variant="body2" sx={{ color: 'primary.main' }}>
            {formatCEHours(content.length)}
            {capce ? ' CAPCE' : ''}
          </Typography>
        </Box>
        {completedDate && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              COMPLETED
            </Typography>
            <Typography variant="body2">
              {formatCertificateDate(completedDate)}
            </Typography>
          </Box>
        )}
        {capce && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              APPROVAL
            </Typography>
            <Typography variant="body2">CAPCE #{capce.courseCode}</Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

// The issued surface. The download is a plain anchor rather than a fetch-and-blob:
// the URL is already a presigned direct link to the PDF, so letting the browser
// follow it is both simpler and better behaved (native download UI, resumable, no
// memory copy of the file).
//
// The link is rendered ONLY from a freshly-polled record. downloadURL is minted
// per response and short-lived, so a link built from anything cached — notably the
// copy sitting in the progress record — hands the learner a 403 from S3. Nothing
// here tells the learner that: the expiry is an implementation detail of how the
// file is served, not a property of their certificate, and saying "your link
// expires" invites them to think the certificate itself is perishable. Every route
// to this surface re-polls, so a fresh link is what they get.
let IssuedCertificate = ({
  content,
  certificate,
}: {
  content: CourseV2Content
  certificate: CourseV2IssuedCertificate
}): ReactElement => {
  let theme = useTheme()
  let user = useAppSelector(selectUser)
  let firstName = user?.firstName ?? null

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: 'auto',
          mb: 3,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${theme.palette.success.main}`,
        }}
      >
        <CertificateIcon sx={{ fontSize: 36, color: 'success.main' }} />
      </Box>

      {/* "Course complete" and not "you're certified": finishing this course
          earns a certificate for THIS course, which is not the same thing as
          holding a certification. Conflating the two would have the app tell a
          learner something about their credentials that isn't ours to say. */}
      <Typography
        variant="h1"
        sx={{ fontSize: '1.75rem', lineHeight: HEADING_LINE_HEIGHT, mb: 1 }}
      >
        {firstName ? `Nice work, ${firstName}!` : 'Nice work!'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Your CE certificate for {content.name} is ready to download.
      </Typography>

      <CertificateSummary
        content={content}
        learnerName={user?.name ?? null}
        certificate={certificate}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mt: 4,
        }}
      >
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<DownloadIcon />}
          href={certificate.downloadURL}
          // The presigned URL points straight at S3, so this leaves the app.
          // noopener/noreferrer because target=_blank without them hands the
          // opened page a handle on this window.
          target="_blank"
          rel="noopener noreferrer"
          sx={{ borderRadius: 8, px: 4 }}
        >
          Download Certificate (PDF)
        </Button>
      </Box>

      {/* Where this certificate lives from now on. Worth saying here because it's
          the one moment the learner is looking at it — a certificate they can find
          again is one they don't need support to recover. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, display: 'block' }}
      >
        You can download this again any time from{' '}
        <Link component={RouterLink} to="/profile/certificates" color="primary">
          your certificates
        </Link>
        .
      </Typography>

      {/* Two ways out, side by side, as in the design handoff. Return is a route
          link rather than a callback: it's ordinary navigation, and the wizard has
          no phase to move to — the learner is done. Restart is the opposite kind of
          thing entirely, a domain event that moves the server's boundary, so it
          isn't navigation at all; the phase gate re-derives this surface into the
          first step once the fresh projection lands.

          Both are text buttons, and restart deliberately isn't the louder of the
          two. It's the more consequential action but not the expected one — the
          learner came here for the PDF, which is the only contained button on the
          page. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mt: 3,
        }}
      >
        <Button
          component={RouterLink}
          to={`/classroom-v2/${content.courseID}`}
          variant="text"
          color="inherit"
          startIcon={<LeftArrowIcon />}
        >
          Return to Overview
        </Button>
        <RestartCourseButton
          courseID={content.courseID}
          courseName={content.name}
        />
      </Box>
    </Box>
  )
}

// Earned and claimed, waiting on the Lambda that renders the PDF. Framed as work
// in progress rather than as a loading state, because that's what it is — the
// learner has finished and nothing they do affects this.
//
// Skeletons rather than a spinner, per the feature's standard, and they earn their
// place here beyond consistency: this screen resolves into IssuedCertificate, so the
// badge and card outlines are the shape of what's genuinely arriving. A spinner
// would say "wait" without saying what for.
//
// The "taking longer" message is gated on how long THIS wait has actually run,
// summed from the poll cadence, and stays hidden until 45s. Below that the skeleton
// alone is the honest answer: the common case resolves in a few seconds, and leading
// with "this is taking longer than usual" would tell almost every learner something
// is wrong at the moment nothing is.
let PendingCertificate = ({
  attempts,
  hasGivenUp,
  onRetry,
}: {
  attempts: number
  hasGivenUp: boolean
  onRetry: () => void
}): ReactElement => {
  let isSlow = hasGivenUp || isCertificateGenerationSlow(attempts)

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
      {/* Same 72px circle as the issued surface's badge, so the certificate icon
          lands in place rather than displacing the layout. */}
      <Skeleton
        variant="circular"
        width={72}
        height={72}
        animation="wave"
        sx={{ mx: 'auto', mb: 3, bgcolor: 'rgba(255,255,255,0.1)' }}
      />
      <Typography
        variant="h1"
        sx={{ fontSize: '1.75rem', lineHeight: HEADING_LINE_HEIGHT, mb: 1 }}
      >
        Generating your certificate
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        You&apos;ve completed everything required. Your CE certificate is being
        prepared — this usually takes a few seconds.
      </Typography>

      {/* Stands in for CertificateSummary, at roughly its height, so the card
          doesn't shove the page around when it arrives. */}
      <Skeleton
        variant="rectangular"
        height={260}
        animation="wave"
        sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.1)' }}
      />

      {/* Sends the learner somewhere useful rather than asking them to sit here:
          the certificate lands in their profile whether this tab is open or not, so
          leaving costs them nothing. Support is named as the second step, for the
          case where it genuinely never arrives. */}
      {isSlow && (
        <Alert severity="info" sx={{ mt: 4, textAlign: 'left' }}>
          This is taking longer than usual. Your completion is safely recorded,
          so nothing is lost — you don&apos;t need to wait here. Check{' '}
          <Link
            component={RouterLink}
            to="/profile/certificates"
            color="inherit"
          >
            your certificates
          </Link>{' '}
          in a little while, and if it still hasn&apos;t appeared, contact
          support and they can look into it.
        </Alert>
      )}

      {/* Only once the loop has given up. While it's still polling, a button
          that does what the page is already doing would only invite the learner
          to think they need to intervene. */}
      {hasGivenUp && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            Check again
          </Button>
        </Box>
      )}

      {/* The wait is announced rather than left to a purely visual skeleton, so a
          screen reader user learns the state has changed. */}
      <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {isSlow
            ? 'Still preparing your certificate.'
            : 'Preparing your certificate…'}
        </Typography>
      </Box>
    </Box>
  )
}

// Requirements outstanding. Reachable in practice only as a disagreement between
// this client and the server — selectWizardPhase already checked the same gates
// before routing here — so it renders as what's left to do rather than as an
// error, exactly like the survey's prerequisitesNotMet. No retry: retrying asks
// the same question and gets the same answer. Finishing the listed work moves the
// progress record, and with it the phase.
let NotEarnedCertificate = ({
  unmetRequirements,
}: {
  unmetRequirements: CourseV2CertificateRequirement[]
}): ReactElement => (
  <Box sx={{ maxWidth: 640, mx: 'auto' }}>
    <Card variant="outlined" sx={{ p: 4 }}>
      <Typography
        variant="h2"
        sx={{ fontSize: '1.4rem', lineHeight: HEADING_LINE_HEIGHT, mb: 1 }}
      >
        Not quite ready for your certificate
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Complete the following, and your certificate will be issued
        automatically:
      </Typography>
      <Box component="ul" sx={{ pl: 3, m: 0 }}>
        {unmetRequirements.map((requirement) => (
          <Typography
            component="li"
            variant="body2"
            key={requirement}
            sx={{ mb: 0.5 }}
          >
            {requirementLabel(requirement)}
          </Typography>
        ))}
      </Box>
    </Card>
  </Box>
)

// Couldn't ask. Deliberately says nothing about whether a certificate exists,
// because a failed request is evidence of nothing — the certificate is very
// likely fine and unreachable only in the sense that we couldn't get an answer.
// Anything stronger would either alarm a learner whose certificate is waiting or
// reassure one whose isn't.
let CertificateUnavailable = ({
  onRetry,
}: {
  onRetry: () => void
}): ReactElement => (
  <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
    <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }} role="alert">
      We couldn&apos;t check on your certificate just now. Your course
      completion is recorded — this is only a problem reaching us.
    </Alert>
    <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRetry}>
      Try again
    </Button>
  </Box>
)

// Wires the poll state to the four surfaces. Same division of labour as QuizStep
// and SurveyStep: no useState, no interval, no fetch — the poll's cadence and its
// stopping rule live in classroom-v2.middleware.ts, and this announces that it's
// showing and renders whatever the store holds. Nothing here can advance the user
// out of the certificate phase, and nothing here decides when to stop asking.
export let CertificateStep = ({
  courseID,
  content,
}: {
  courseID: string
  content: CourseV2Content
}): ReactElement => {
  let dispatch = useAppDispatch()
  let poll = useAppSelector(selectCertificatePollByCourseID(courseID))

  // Being in the certificate phase IS the cue to start polling — the phase came
  // from the server saying this learner has earned it, so there's no user action
  // to wait for. A null poll record is the only trigger, so this can't restart a
  // loop that's already running or one that has already finished.
  useEffect(() => {
    if (poll === null) dispatch(certificatePollRequested(courseID))
  }, [poll === null, courseID])

  let retry = () => dispatch(certificatePollRequested(courseID))

  if (poll === null) {
    return (
      <PendingCertificate attempts={0} hasGivenUp={false} onRetry={retry} />
    )
  }

  if (poll.kind === 'failed') {
    // A previously-issued certificate still renders — the learner has it, and
    // failing to re-ask is no reason to take the surface away.
    return poll.last?.kind === 'issued' ? (
      <IssuedCertificate
        content={content}
        certificate={poll.last.certificate}
      />
    ) : (
      <CertificateUnavailable onRetry={retry} />
    )
  }

  // The last known answer, whether the poll has come to rest on it or is still
  // asking. Rendering the in-flight case from `last` is what stops the surface
  // flickering between a real state and a spinner every couple of seconds.
  let certificate: CourseV2Certificate | null =
    poll.kind === 'answered' ? poll.certificate : poll.last

  if (certificate?.kind === 'issued') {
    return (
      <IssuedCertificate
        content={content}
        certificate={certificate.certificate}
      />
    )
  }

  if (certificate?.kind === 'notEarned') {
    return (
      <NotEarnedCertificate unmetRequirements={certificate.unmetRequirements} />
    )
  }

  // Pending, or nothing back yet — the same screen either way, since both mean
  // "earned, still waiting".
  //
  // hasGivenUp needs BOTH halves. An answered-but-unresolved poll is not evidence
  // the loop has stopped: it's the state after every single pending response, held
  // for the whole delay before the next request goes out. Testing only that made
  // "give up" true two seconds into a perfectly normal wait — which surfaced the
  // "Check again" button and, because hasGivenUp forces isSlow, defeated the 45s
  // gate on "this is taking longer than usual" entirely.
  //
  // The attempt ceiling is what actually distinguishes the two, and it's read from
  // the shared cadence module so this can't disagree with the loop that enforces it.
  return (
    <PendingCertificate
      attempts={poll.attempts}
      hasGivenUp={
        poll.kind === 'answered' &&
        !isCertificateResolved(poll.certificate) &&
        poll.attempts >= CERTIFICATE_POLL_MAX_ATTEMPTS
      }
      onRetry={retry}
    />
  )
}
